"use strict";

import UserStore from "@stores/UserStore";
import basicMealConditions from "@tables/basic-conditions";
import allNutrients from "@tables/nutrients";
import allRecommendations from "@tables/recommendations";
import allTags from "@tables/tags";
import { fetchDocumentsById } from "@utils/Content";
import { getDietsFromTags } from "@utils/Diets";
import { roundForHumans } from "@utils/Math";
import { getDemographic, nutrNoSortCmp } from "@utils/Nutrition";
import { fetchVirtualPlan } from "@utils/Plans";
import { isEmpty, isEqual } from 'lodash';
import indexBy from "lodash.indexby";
import moment from "moment";
import LocalStorage from "store";
import uuid from 'uuid';

const mealTypes = {
    Breakfast: { s: "breakfast", p: "breakfasts" },
    Lunch: { s: "lunch", p: "lunches" },
    Dinner: { s: "dinner", p: "dinners" },
    Snack: { s: "snack", p: "snacks" },
};

export function getExpirationInfo(active_until, expiration) {
    let expirationObj = {};

    if (active_until && expiration !== "unlimited") {
        const day_diff = moment(active_until).startOf("day").diff(moment().startOf("day"), "days");
        const expired = day_diff < 0;
        const expiring_soon = !expired && day_diff <= 14;
        expirationObj = { expired, expiring_soon, days: Math.abs(day_diff) };
    }

    return expirationObj;
}

export function computeBmi(weightKg, heightCm) {
    const heightMeters = heightCm / 100;

    if (!weightKg || !heightCm) {
        return false;
    }

    const bmi = weightKg / (heightMeters * heightMeters);

    // Round to tenths
    return Math.round(bmi * 10) / 10;
}

export function cmToFeetInches(cm) {
    if (!cm) {
        return false;
    }

    // Convert to inches first.
    let inches = cm * 0.393701,
        feet = Math.floor(inches / 12);

    if (feet) {
        inches -= feet * 12;
    }

    inches = Math.round(inches);

    // Special case rounding up to next foot
    if (inches == 12) {
        feet += 1;
        inches = 0;
    }

    return { feet, inches };
}

export function getPatientDemographic(patient, demographics) {
    let patientDemo = "default";

    // Get patient age, gender, trimester, lactation flag
    const age = roundForHumans(moment().diff(patient.birthdate, "years", true));

    Object.keys(demographics).forEach((key) => {
        const { min_age, max_age, gender, trimester, lactating } = demographics[key];

        if (typeof min_age === "number" && age < min_age) {
            return;
        }

        if (typeof max_age === "number" && age > max_age) {
            return;
        }

        if (typeof gender !== "undefined" && patient.gender !== gender) {
            return;
        }

        patientDemo = key;
    });

    return patientDemo;
}

export function convertEnvelopeToFilters(envelope, min = "min", max = "max", prefix = "", factor = 1) {
    const filters = {};

    Object.keys(envelope).forEach((nutrNo) => {
        const nutrient = allNutrients[nutrNo];

        if (!(nutrient && nutrient.Filter)) {
            return;
        }

        const range = {};

        if (typeof envelope[nutrNo].min === "number") {
            range[min] = envelope[nutrNo].min / factor;
        } else if (typeof envelope[nutrNo].implicit?.min === 'number') {
            range[min] = envelope[nutrNo].implicit.min / factor;
        }

        if (typeof envelope[nutrNo].max === "number") {
            range[max] = envelope[nutrNo].max / factor;
        } else if (typeof envelope[nutrNo].implicit?.max === 'number') {
            range[max] = envelope[nutrNo].implicit.max / factor;
        }

        if (Object.keys(range).length > 0) {
            filters[prefix + nutrient.Filter] = range;
        }
    });

    return filters;
}

export function addPrescriptionFiltersToParams(profile, params, mealType) {
    const allDayRx = profile?.preferences?.daily_totals;
    let mealRx = mealType.envelope;

    if (!allDayRx || !mealRx) {
        return params;
    }

    Object.assign(params.filters, convertEnvelopeToFilters(mealRx, "gte", "lte", "", profile.portion));

    return params;
}

export function setRecommendedModeSourceParameters(mode, params, profile) {
    const {
        recommendation_mode_source
    } = profile;

    if (recommendation_mode_source && recommendation_mode_source[mode]) {
        const { boost, filter, exclude } = recommendation_mode_source[mode];

        const boostMerchants = boost?.filter(o => o?.source_type === "merchant");

        if (boostMerchants?.length) {
            params.sort_params.boosts = boostMerchants.map(o => ({
                "merchant.uuid": o.uuid,
                "weight": o.meta?.boost_value,
            }));

            params.include_merchants = boostMerchants.map(o => o.uuid);
        }

        const filterMerchants = filter?.filter(o => o?.source_type === "merchant");

        if (filterMerchants?.length) {
            params.filters = {
                ...params.filters,
                "merchant.uuid": filterMerchants.map(o => o.uuid),
            };
        }

        const excludeMerchants = exclude?.filter((obj) => obj?.source_type === "merchant");
        const excludeMerchantsMap = excludeMerchants?.map((obj) => obj?.uuid);

        if (excludeMerchants) {
            params.filters = {
                ...params.filters,
                "!merchant.uuid": excludeMerchantsMap,
            };
        }
    }
}

export function getMealSearchParamsForProfile(mealType, profile) {
    const { preferences = {} } = profile;

    const {
        diets = [],
        avoidances = [],
        exclude_foods = [],
        equipment = [],
        skill_level,
        limit_tags = [],
        avoid_cuisines = [],
        prefer_cuisines = [],
        prefer_milks = [],
        prefer_rices = [],
    } = preferences;

    const excludeTags = []
        .concat(avoid_cuisines)
        .concat(allTags.equipment.tags.filter((tag) => !equipment.includes(tag)));

    let params = {
        types: ["recipe", "combo"],
        filters: {},
        sort_by: "ideal_smart_choices",
        sort_params: {
            prefers: prefer_milks.concat(prefer_rices).concat(prefer_cuisines),
            avoidances: avoidances.slice(0),
        },
        include_merchants: profile?.features?.source_libraries || null
    };

    if (limit_tags.length) {
        params.filters["tags"] = limit_tags.slice(0);
    }

    if (excludeTags.length) {
        params.filters["!tags"] = excludeTags;
    }

    if (avoidances.length) {
        params.filters["!ingredient_tags"] = avoidances.slice(0);
    }

    if (exclude_foods) {
        params.filters["!foods"] = exclude_foods.slice(0);
    }

    if (profile.pregnant) {
        params.filters["!tags"] = params.filters["!tags"] || [];
        params.filters["!tags"].push("NOPREG");
        params.filters["!ingredient_tags"].push("NOPREG");
    }

    getDietsFromTags(diets).forEach((diet) => {
        // Find the intersection of the diet avoidances and the prefs.avoidances
        var intersect = diet.avoidances.filter((avoidance) => avoidances.includes(avoidance));
        if (intersect.length === diet.avoidances.length) {
            params.filters.tags = params.filters.tags || [];
            params.filters.tags.push(diet.name);
        }
    });

    // If the profile is a kid, use only kid friendly recipes. This will
    // severely limit the amount of content available to generate with, but
    // is better than showing wildly inappropriate recipes for a kid.
    if (profile.birthdate) {
        const age = moment().diff(profile.birthdate, "year");

        if (age <= 12) {
            params.filters.tags = params.filters.tags || [];
            params.filters.tags.push("Kid Friendly");
        }
    }

    if (skill_level === "Beginner") {
        params.filters["!tags"] = params.filters["!tags"] || [];
        params.filters["!tags"].push("Intermediate");
        params.filters["!tags"].push("Advanced");
    }

    if (skill_level === "Intermediate") {
        params.filters["!tags"] = params.filters["!tags"] || [];
        params.filters["!tags"].push("Advanced");
    }

    if (mealType.main_dish == "Breakfast") {
        params.filters.tags = params.filters.tags || [];
        params.filters.tags.push("Breakfast");
    }
    if (mealType.main_dish == "Lunch") {
        params.filters.tags = params.filters.tags || [];
        params.filters.tags.push("Lunch");
    }
    if (mealType.main_dish == "Dinner") {
        params.filters.tags = params.filters.tags || [];
        params.filters.tags.push("Dinner");
    }
    if (mealType.main_dish == "Snack") {
        params.types = ["recipe"];
        params.filters.tags = params.filters.tags || [];
        params.filters.tags.push("Snack");
    }

    addPrescriptionFiltersToParams(profile, params, mealType);

    return params;
}

export function getParamsForProfile(profile) {
    const { preferences = {}, conditions = [], family = [], shopping_freq = 1, language = 'en', portion = 1 } = profile;

    const params = {
        portion,
        language,
        conditions,
        family,
        days: shopping_freq,
        preference_order: [preferences?.uuid],
        preferences: [preferences],
    };

    return params;
}

const STORED_PLANS_KEY = "patient-recommended-plans";

export function removeStoredPlans(patient) {
    const user = UserStore.getUser();

    if (!user) {
        return;
    }

    const key = [STORED_PLANS_KEY, patient.uuid, user.uuid].join("-");

    LocalStorage.remove(key);
}

export function loadStoredPlans(patient) {
    const user = UserStore.getUser();

    if (!user) {
        return Promise.reject();
    }

    const key = [STORED_PLANS_KEY, patient.uuid, user.uuid].join("-");
    let memory = LocalStorage.get(key) || {};
    let { plans = [], virtual = [], total = 0, expires = null, lastSearchParams, lastGenParams } = memory;

    // If a virtual plan in the list is no longer available, remove it.
    virtual = virtual.map((uuid) => fetchVirtualPlan(uuid)).filter((v) => v);

    // Stored plans will contain an array of IDs. We need to load the ones not marked
    // virtual and pull the other ones from the virtual meal plan store.
    return new Promise((accept, reject) => {
        // If there is no expires or it is expired, return nothing and delete.
        if (!expires || (expires && moment().isAfter(expires))) {
            removeStoredPlans(patient);

            return accept({
                plans: [],
                virtual: [],
                total: 0,
                lastSearchParams: null,
                lastGenParams: null,
            });
        }

        if (!plans.length) {
            return accept({
                plans: [],
                virtual,
                total,
                lastSearchParams,
                lastGenParams,
            });
        }

        fetchDocumentsById(plans).then((documents) => {
            return accept({
                plans: documents,
                virtual,
                total,
                lastSearchParams,
                lastGenParams,
            });
        });
    });
}

export function setStoredPlans(patient, virtual, lastGenParams, explain = null) {
    const user = UserStore.getUser();

    if (!user) {
        return;
    }

    // Expire in 42 days
    const expires = moment().add(42, "day");
    const key = [STORED_PLANS_KEY, patient.uuid, user.uuid].join("-");

    LocalStorage.set(
        key,
        {
            virtual: virtual.filter((v) => v).map((v) => v.uuid),
            expires: expires.format(),
            lastGenParams,
        },
        expires.toDate().getTime(),
    );
}

const HIDE_PATIENT_KEY = "patient-feed";

export function hidePatientFor(patient, days) {
    const user = UserStore.getUser();

    if (!user) {
        return;
    }

    const expires = moment().add(days, "day").format();
    const key = [HIDE_PATIENT_KEY, patient.uuid, user.uuid].join("-");

    LocalStorage.set(key, { expires });
}

export function isPatientHidden(patient) {
    const user = UserStore.getUser();

    if (!user) {
        return;
    }

    const key = [HIDE_PATIENT_KEY, patient.uuid, user.uuid].join("-");

    let { expires } = LocalStorage.get(key) || {};

    if (!expires || (expires && moment().isAfter(expires))) {
        LocalStorage.remove(key);

        return false;
    }

    return true;
}

export function isPatientChild(patient) {
    const birthdate = (patient && patient.birthdate) || null;

    if (!birthdate) {
        return false;
    }

    const age = roundForHumans(moment().diff(patient.birthdate, "years", true));

    return age <= 12;
}

export function getRecipeMismatchesForProfile(recipe, meal_type, profile) {
    let mismatches = [];

    if (!profile) {
        return mismatches;
    }

    const { preferences = {}, portion = 1 } = profile;
    const { meal_types = [], daily_totals } = preferences;

    // Find meal type specific envelope
    let envelope = null;
    const mealType = meal_types.find((mt) => mt.name === meal_type);

    if (mealType && mealType.envelope) {
        envelope = mealType.envelope;
    } else if (daily_totals) {
        // Fallback to daily totals if no specific meal type found
        envelope = daily_totals;
    }

    // If we STILL don't have an envelope, I guess we pass?
    if (!envelope || !(recipe.nutrients && recipe.nutrients.values)) {
        return mismatches;
    }

    Object.keys(envelope).forEach((nutrNo) => {
        const { min = 0, max = 99999 } = envelope[nutrNo];

        const value = (Math.round((recipe.nutrients.values[nutrNo] || 0) * 100) / 100) * portion;

        // If the recipe has too much of something, disqualify it
        if (value > max) {
            mismatches.push({
                meal_type: mealType ? mealType.name : 'all-day',
                nutrNo,
                value,
                min,
                max,
            });
        }
    });

    return mismatches;
}

const alwaysShowNutrNos = [
    "208", // calories
    "205", // carbs
    "203", // protein
    "204", // fat
    "606", // saturated fat
    "605", // trans fat
    "601", // cholesterol
    "307", // sodium
    "291", // fiber
    "269", // total sugar
    "ASG", // added sugar
    "324", // vitamin d
    "301", // calcium
    "303", // iron
    "306", // potassium
    "305", // phosphorus
    "FRU", // fruits
    "VEG", // vegetables
];

export function getNutrNosForProfile(profile) {
    const nutrNos = alwaysShowNutrNos.slice();
    const { preferences = {}, portion = 1, target_energy_kcal = 2000 } = profile;
    const allDay = preferences.daily_totals || {};

    Object.keys(allDay).forEach((nutrNo) => {
        if (!nutrNos.includes(nutrNo)) {
            nutrNos.push(nutrNo);
        }
    });

    nutrNos.sort(nutrNoSortCmp);

    return nutrNos;
}

export function getNutrientsToDisplay(profile, values, unavailableDefault = 0, showAllAvailable) {
    const demographic = getDemographic(profile);
    const dri = {
        ...allRecommendations["all-ages"],
        ...allRecommendations[demographic],
    };

    // Initialize nutrients and set to their DRI values for this profile
    const nutrients = {};
    alwaysShowNutrNos.forEach((nutrNo) => {
        nutrients[nutrNo] = {
            v: values[nutrNo] == null ? unavailableDefault : values[nutrNo],
            t: dri[nutrNo] || unavailableDefault,
            targetName: 'DRI',
        };
    });

    // If there is no profile, show all the default nutrients.
    if (!profile?.preferences?.daily_totals) {
        return nutrients;
    }

    const { preferences = {}, portion = 1, target_energy_kcal = 2000 } = profile;

    // Update the pro/cho/fat DRIs to fit with the target energy needs
    dri[203] = Math.round((target_energy_kcal * 0.2) / 4); // calories from protein should be 20%
    dri[204] = Math.round((target_energy_kcal * 0.3) / 9); // total fat should be 30% of daily calorie intake
    dri[205] = Math.round((target_energy_kcal * 0.5) / 4); // total carbs should be 50% of daily calorie intake

    // Just use the all-day prescription for now
    const allDay = preferences.daily_totals || {};

    let nonDefaultNutrientsToDisplay = Object.keys(allDay);

    nonDefaultNutrientsToDisplay = nonDefaultNutrientsToDisplay.concat(alwaysShowNutrNos.filter(
        nutrNo => !nonDefaultNutrientsToDisplay.includes(nutrNo)
    ));

    if (showAllAvailable) {
        const otherNutrients = Object.keys(values).filter(
            (nutrNo) => !alwaysShowNutrNos.includes(nutrNo) && !nonDefaultNutrientsToDisplay.includes(nutrNo),
        );
        nonDefaultNutrientsToDisplay = nonDefaultNutrientsToDisplay.concat(otherNutrients);
    }

    nonDefaultNutrientsToDisplay.forEach((nutrNo) => {
        const range = allDay[nutrNo];

        let target = dri[nutrNo];
        let value = values[nutrNo] == null ? unavailableDefault : values[nutrNo];
        let targetName = null;
        let trackedOnly = false;

        if (value != null) {
            value = value * portion;
        }

        if (range && range.min && range.max) {
            target = (range.min + range.max) / 2;
            targetName = "goal";
        } else if (range && range.max) {
            target = range.max;
            targetName = "max";
        } else if (range && range.min) {
            target = range.min;
            targetName = "goal";
        } else {
            target = dri[nutrNo] || unavailableDefault;
        }

        // Consider it 100%
        if (!target || isNaN(target)) {
            target = dri[nutrNo] || value;
            trackedOnly = true;
        }

        nutrients[nutrNo] = {
            v: value,
            t: target,
            targetName,
            trackedOnly,
        };
    });

    return nutrients;
}

// Can accept a patient or a user. Both the schemas mirror where it counts
export function getConfigurationIssues(patient) {
    let { target_energy_kcal, birthdate, gender, conditions = [], preferences = {}, portion = 1, portion_resolution } = patient;
    let { diets = [], avoidances = [], limit_tags = [] } = preferences;

    // Default age to 25 if we don't have a birthdate
    const age = birthdate ? moment().diff(birthdate, "years") : 25;

    // Convert conditions to array of conditions and condition names
    const conditionNames = conditions.map((c) => c.name).filter((c) => c);

    if (conditionNames.length === 0) {
        return {
            warnings: [],
            errors: [],
        };
    }

    // Determine if user is vegetarian
    const veggies = ["Vegetarian (eggs & dairy OK)", "Vegetarian (no eggs)", "Vegetarian (no dairy)"];
    const isVegetarian = diets.filter((d) => veggies.includes(d)).length > 0;

    const warnings = [];
    const errors = [];

    // Iron Deficient Anemia
    if (conditionNames.includes("Anemia Iron Deficient") && diets.includes("Vegan") && age <= 12) {
        warnings.push(
            "Iron deficient anemia vegan diets are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Anemia Iron Deficient") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("nightshades")
    ) {
        warnings.push(
            "Low calorie plans for iron deficient anemia and avoiding nightshades are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Anemia Iron Deficient") &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Plans for iron deficient anemia avoiding nightshades, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Anemia Iron Deficient") &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Plans for iron deficient anemia avoiding corn, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // B-12 Deficient Anemia
    if (conditionNames.includes("Anemia B12 Deficient") && diets.includes("Vegan")) {
        errors.push(
            "We're sorry, the B-12 deficient anemia condition is incompatible with the Vegan diet. Please revise your profile.",
        );
    }

    if (
        conditionNames.includes("Anemia B12 Deficient") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Lower calorie plans for B-12 deficient anemia avoiding nightshades, wheat and dairy are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Anemia B12 Deficient") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        age <= 12 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Plans for pediatric B-12 deficient anemia and avoiding nightshades, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Anemia B12 Deficient") &&
        target_energy_kcal < 1200 &&
        target_energy_kcal > 200 &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Plans for pediatric B-12 deficient anemia and avoiding corn, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Bariatric Stage 4
    if (conditionNames.includes("Bariatric Stage 4") && diets.includes("Vegan")) {
        warnings.push(
            "Bariatric vegan diets are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("Bariatric Stage 4") && isVegetarian) {
        warnings.push(
            "Bariatric vegetarian diets are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    // Chronic Kidney Disease (CKD)
    if (
        conditionNames.includes("CKD Stage 1-2") &&
        isVegetarian &&
        avoidances.includes("treenuts") &&
        avoidances.includes("wheat")
    ) {
        warnings.push(
            "Kidney-friendly vegetarian plans with no treenuts and gluten free are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("CKD Stage 1-2") && target_energy_kcal > 2500 && target_energy_kcal > 200) {
        warnings.push(
            "Higher calorie kidney-friendly diets are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("CKD Stage 1-2") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("soy")
    ) {
        warnings.push(
            "Plans for this profile are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("CKD Stage 1-2") &&
        target_energy_kcal > 2200 &&
        target_energy_kcal > 200 &&
        avoidances.includes("wheat")
    ) {
        warnings.push(
            "Plans for this profile are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("CKD Stage 1-2") && diets.includes("Vegan")) {
        warnings.push(
            "Kidney-friendly vegan diets are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("CKD Stage 1-2") &&
        target_energy_kcal < 1200 &&
        target_energy_kcal > 200 &&
        avoidances.includes("avocado") &&
        avoidances.includes("cilantro")
    ) {
        warnings.push(
            "Low calorie plans for CKD and avoiding avocados and cilantro are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("CKD Stage 1-2") &&
        target_energy_kcal > 2000 &&
        target_energy_kcal > 200 &&
        avoidances.includes("nightshades")
    ) {
        warnings.push(
            "High calorie plans for CKD avoiding nightshades are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("CKD Stage 1-2") &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Plans for CKD with these avoidances are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("CKD Stage 1-2") &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Plans for CKD with these avoidances are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Diabetes
    if (
        isVegetarian &&
        age <= 12 &&
        (conditionNames.includes("Diabetes Type 1") || conditionNames.includes("Diabetes Type 2"))
    ) {
        warnings.push(
            "Plans for kid friendly, vegetarian and diabetes are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        target_energy_kcal < 1100 &&
        target_energy_kcal > 200 &&
        age <= 12 &&
        avoidances.includes("milk") &&
        (conditionNames.includes("Diabetes Type 1") || conditionNames.includes("Diabetes Type 2"))
    ) {
        warnings.push(
            "Low calorie dairy free diabetes profiles are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        diets.includes("Vegan") &&
        (conditionNames.includes("Diabetes Type 1") || conditionNames.includes("Diabetes Type 2"))
    ) {
        warnings.push(
            "Low calorie vegan diabetes profiles are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        (conditionNames.includes("Diabetes Type 1") || conditionNames.includes("Diabetes Type 2")) &&
        age <= 12 &&
        diets.includes("Vegan")
    ) {
        errors.push(
            "We're sorry, the diabetes conditions are incompatible with the vegan diet for children. Please revise your profile.",
        );
    }

    if (
        (conditionNames.includes("Diabetes Type 1") || conditionNames.includes("Diabetes Type 2")) &&
        isVegetarian &&
        avoidances.includes("treenuts") &&
        avoidances.includes("wheat")
    ) {
        warnings.push(
            "Diabetes vegetarian plans with no treenuts and gluten free are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    // Prediabetes
    if (
        conditionNames.includes("Prediabetes") &&
        isVegetarian &&
        target_energy_kcal < 1200 &&
        target_energy_kcal > 200 &&
        age <= 12
    ) {
        warnings.push(
            "Low calorie vegetarian plans for prediabetes are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("Prediabetes") && diets.includes("Vegan") && age <= 12) {
        warnings.push(
            "Vegan plans for prediabetes are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Prediabetes") &&
        target_energy_kcal < 1200 &&
        target_energy_kcal > 200 &&
        avoidances.includes("milk") &&
        age <= 12
    ) {
        warnings.push(
            "Low calorie plans for prediabetes avoiding dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Prediabetes") &&
        target_energy_kcal < 1200 &&
        target_energy_kcal > 200 &&
        avoidances.includes("eggs") &&
        age <= 12
    ) {
        warnings.push(
            "Low calorie plans for prediabetes avoiding eggs are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Prediabetes") &&
        isVegetarian &&
        age <= 12 &&
        avoidances.includes("wheat") &&
        avoidances.includes("treenuts")
    ) {
        warnings.push(
            "Plans for vegetarian prediabetes avoiding wheat and treenuts are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Prediabetes") &&
        target_energy_kcal > 2000 &&
        age <= 12 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Higher calorie plans for prediabetes avoiding nightshades, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Prediabetes") &&
        target_energy_kcal > 2000 &&
        age <= 12 &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "High calorie plans for pediatric prediabetes avoiding corn, wheat and milk are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Diverticulitis
    if (conditionNames.includes("Diverticulitis") && isVegetarian) {
        warnings.push(
            "Low fiber vegetarian diets are not fully supported at the moment. It is challenging to fulfill a low fiber vegetarian diet with a whole foods approach. We are adding new content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("Diverticulitis") && diets.includes("Vegan")) {
        errors.push(
            "We're sorry, the diverticulitis condition is incompatible with the vegan diet. It is challenging to fulfill a low fiber vegan diet with a whole foods approach. Please revise your profile.",
        );
    }

    if (
        conditionNames.includes("Diverticulitis") &&
        avoidances.includes("milk") &&
        target_energy_kcal > 2000 &&
        target_energy_kcal > 200
    ) {
        warnings.push(
            "Low fiber higher calorie dairy-free diets are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Diverticulitis") &&
        avoidances.includes("corn") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200
    ) {
        warnings.push(
            "Low calorie plans for diverticulitis and avoiding corn are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Diverticulitis") &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk") &&
        target_energy_kcal > 1600 &&
        target_energy_kcal > 200
    ) {
        warnings.push(
            "Plans for diverticulitis and avoiding nightshades, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Diverticulitis") &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk") &&
        target_energy_kcal > 1500 &&
        target_energy_kcal > 200
    ) {
        warnings.push(
            "Plans for diverticulitis and avoiding corn, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Heart Failure
    if (conditionNames.includes("Heart Failure") && diets.includes("Vegan")) {
        warnings.push(
            "Vegan plans for heart failure are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("Heart Failure") && avoidances.includes("milk")) {
        warnings.push(
            "Plans for heart failure and avoiding dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Heart Failure") &&
        target_energy_kcal < 1600 &&
        target_energy_kcal > 200 &&
        avoidances.includes("soy")
    ) {
        warnings.push(
            "Low calorie plans for heart failure and avoiding soy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Heart Failure") &&
        target_energy_kcal < 1800 &&
        target_energy_kcal > 200 &&
        isVegetarian &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie vegetarian plans for heart failure and avoiding wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Heart Failure") &&
        target_energy_kcal < 1800 &&
        target_energy_kcal > 200 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie plans for heart failure and avoiding nightshades, wheat, and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Heart Failure") &&
        target_energy_kcal < 1800 &&
        target_energy_kcal > 200 &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie plans for heart failure and avoiding corn, wheat, and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Hypertension
    if (
        conditionNames.includes("Hypertension") &&
        diets.includes("Vegan") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200
    ) {
        warnings.push(
            "Plans for hypertension on a Vegan diet are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypertension") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("nightshades")
    ) {
        warnings.push(
            "Low calorie plans for hypertension avoiding nightshades diet are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypertension") &&
        isVegetarian &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200
    ) {
        warnings.push(
            "Low calorie vegetarian plans for hypertension are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypertension") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("eggs")
    ) {
        warnings.push(
            "Low calorie plans for Hypertension and avoiding eggs are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypertension") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("peanuts")
    ) {
        warnings.push(
            "Low calorie plans for Hypertension and avoiding peanuts are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypertension") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("shellfish")
    ) {
        warnings.push(
            "Low calorie plans for Hypertension and avoiding shellfish are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypertension") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("corn")
    ) {
        warnings.push(
            "Low calorie plans for Hypertension and avoiding corn are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypertension") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("avocado") &&
        avoidances.includes("cilantro")
    ) {
        warnings.push(
            "Low calorie plans for Hypertension and avoiding avocado and cilantro are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Hypermetabolism
    if (
        conditionNames.includes("Hypermetabolism") &&
        target_energy_kcal < 1600 &&
        target_energy_kcal > 200 &&
        diets.includes("Vegan")
    ) {
        warnings.push(
            "Low calorie vegan plans for hypermetabolism are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypermetabolism") &&
        target_energy_kcal < 1600 &&
        target_energy_kcal > 200 &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie plans for hypermetabolism and avoiding dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypermetabolism") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("soy")
    ) {
        warnings.push(
            "Low calorie plans for hypermetabolism and avoiding soy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypermetabolism") &&
        target_energy_kcal < 1900 &&
        target_energy_kcal > 200 &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie plans for hypermetabolism and avoiding corn, wheat, and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypermetabolism") &&
        target_energy_kcal > 3000 &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "High calorie plans for hypermetabolism and avoiding corn, wheat, and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypermetabolism") &&
        target_energy_kcal < 2000 &&
        target_energy_kcal > 200 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie plans for hypermetabolism and avoiding nightshades, wheat, and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypermetabolism") &&
        target_energy_kcal > 2800 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "High calorie plans for hypermetabolism and avoiding nightshades, wheat, and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Hypermetabolism") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("treenuts")
    ) {
        warnings.push(
            "Low calorie plans for hypermetabolism and avoiding treenuts are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Low Sodium
    if (conditionNames.includes("Low Sodium") && isVegetarian && target_energy_kcal > 2000) {
        warnings.push(
            "Higher calorie low sodium vegetarian plans are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("Low Sodium") && diets.includes("Vegan")) {
        warnings.push(
            "Low sodium vegan plans are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        isVegetarian &&
        avoidances.includes("wheat") &&
        avoidances.includes("treenuts")
    ) {
        warnings.push(
            "Vegetarian low calorie plans for low sodium and avoiding wheat and treenuts are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal > 2200 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "High calorie plans for low sodium and avoiding nightshades, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("Low Sodium") && isVegetarian && age <= 12) {
        warnings.push(
            "Vegetarian plans for pediatric low sodium are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal < 1200 &&
        target_energy_kcal > 200 &&
        age <= 12 &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Plans for pediatric low sodium and avoiding dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal > 2000 &&
        age <= 12 &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "High calorie plans for pediatric low sodium and avoiding dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal > 2200 &&
        age <= 12 &&
        avoidances.includes("treenuts")
    ) {
        warnings.push(
            "High calorie plans for pediatric low sodium and avoiding treenuts are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("Low Sodium") && target_energy_kcal > 2200 && age <= 12 && avoidances.includes("soy")) {
        warnings.push(
            "High calorie plans for pediatric low sodium and avoiding soy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal < 1200 &&
        target_energy_kcal > 200 &&
        age <= 12 &&
        avoidances.includes("corn")
    ) {
        warnings.push(
            "Plans for pediatric low sodium and avoiding corn are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal < 1200 &&
        target_energy_kcal > 200 &&
        age <= 12 &&
        avoidances.includes("nightshades")
    ) {
        warnings.push(
            "Plans for pediatric low sodium and avoiding nightshades are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal > 2000 &&
        age <= 12 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "High calorie plans for pediatric low sodium and avoiding nightshades, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Low Sodium") &&
        target_energy_kcal > 1900 &&
        age <= 12 &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "High calorie plans for pediatric low sodium and avoiding corn, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Osteoporosis
    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("nightshades") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie plans for osteoporosis and avoiding nightshades, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("corn") &&
        avoidances.includes("wheat") &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie plans for osteoporosis and avoiding corn, wheat and dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (conditionNames.includes("Osteoporosis") && diets.includes("Vegan")) {
        warnings.push(
            "Vegan plans for Osteoporosis are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("wheat")
    ) {
        warnings.push(
            "Low calorie plans for Osteoporosis and avoiding wheat are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1600 &&
        target_energy_kcal > 200 &&
        avoidances.includes("milk")
    ) {
        warnings.push(
            "Low calorie plans for Osteoporosis and avoiding dairy are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("eggs")
    ) {
        warnings.push(
            "Low calorie plans for Osteoporosis and avoiding eggs are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("treenuts")
    ) {
        warnings.push(
            "Low calorie plans for Osteoporosis and avoiding treenuts are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("fish")
    ) {
        warnings.push(
            "Low calorie plans for Osteoporosis and avoiding treenuts are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1600 &&
        target_energy_kcal > 200 &&
        avoidances.includes("shellfish")
    ) {
        warnings.push(
            "Low calorie plans for Osteoporosis and avoiding shellfish are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    if (
        conditionNames.includes("Osteoporosis") &&
        target_energy_kcal < 1400 &&
        target_energy_kcal > 200 &&
        avoidances.includes("corn")
    ) {
        warnings.push(
            "Low calorie plans for Osteoporosis and avoiding corn are not fully supported at the moment. We’re adding more content daily. Expect limited results.",
        );
    }

    // Pregancy
    if (conditionNames.includes("Pregnancy") && diets.includes("Vegan") && target_energy_kcal > 2400) {
        warnings.push(
            "Higher calorie pregnancy vegan diets are not fully supported at the moment. We are adding new content daily. Expect limited results.",
        );
    }

    // Fat Loss
    if (conditionNames.includes("Fat Loss")) {
        if (diets.includes("Vegan") || isVegetarian) {
            errors.push(
                "We're sorry! Fat Loss lifestyle goal is not compatible with vegetarian or vegan diets due to the high protein requirement.",
            );
        }

        if (target_energy_kcal >= 2600) {
            errors.push(
                "We're sorry! Fat Loss lifestyle goal is not supported above 2600 calories per day. Please revise your profile.",
            );
        }
    }

    if (conditionNames.includes("Endurance")) {
        if (diets.includes("Vegan") && target_energy_kcal >= 3000) {
            warnings.push(
                "Endurance lifestyle goal is not fully supported for very high calorie vegans. We’re adding more content daily. Expect limited results!",
            );
        }
    }

    if (conditionNames.includes("Muscle Gain")) {
        if (diets.includes("Vegan") && target_energy_kcal > 2600) {
            errors.push(
                "We're sorry! Muscle Gain lifestyle goal is not supported for vegans. Please revise your profile.",
            );
        }

        if (isVegetarian && target_energy_kcal > 2600) {
            warnings.push(
                "Muscle Gain lifestyle goal is not fully supported for very high calorie vegans. We’re adding more content daily. Expect limited results!",
            );
        }

        if (diets.includes("Vegan") && target_energy_kcal <= 1500) {
            warnings.push(
                "Muscle Gain lifestyle goal is not fully supported for low calorie vegans. We’re adding more content daily. Expect limited results!",
            );
        }
    }

    if (conditionNames.includes("Low Carb/High Fat")) {
        if (diets.includes("Vegan")) {
            errors.push(
                "We're sorry! Low Carb/High Fat lifestyle goal is not supported for vegans. Please revise your profile.",
            );
        }

        if (isVegetarian) {
            warnings.push(
                "Low Carb/High Fat lifestyle goal is not fully supported for vegetarians. We’re adding more content daily. Expect limited results!",
            );
        }

        if (avoidances.includes("eggs") && target_energy_kcal >= 3500) {
            warnings.push(
                "Low Carb/High Fat lifestyle goal is not fully supported for those with high-calorie needs and avoiding eggs. We’re adding more content daily. Expect limited results!",
            );
        }

        if (avoidances.includes("treenuts") && target_energy_kcal >= 3500) {
            warnings.push(
                "Low Carb/High Fat lifestyle goal is not fully supported for those with high-calorie needs and avoiding treenuts. We’re adding more content daily. Expect limited results!",
            );
        }
    }

    if (conditionNames.includes("Low FODMAP") && avoidances.length > 0) {
        warnings.push(
            "Low FODMAP is not fully supported for those with many common food allergies and avoidances. Results may vary.",
        );
    }

    const basicConds = conditionNames.filter((cn) => basicMealConditions.includes(cn));

    if (limit_tags.includes("Basic") && basicConds.length !== conditionNames.length) {
        warnings.push("NOT RECOMMENDED: Given your nutrition requirements, we recommend turning Simple Recipes OFF.");
    } else if (target_energy_kcal < 1400 && limit_tags.includes("Basic")) {
        warnings.push(
            "Simple Recipes are not yet available for nutrition requirements of less than 1400 calories per day.",
        );
    } else if (target_energy_kcal >= 2400 && limit_tags.includes("Basic")) {
        warnings.push(
            "Simple Recipes are not yet available for nutrition requirements of more than 2400 calories per day.",
        );
    } else if (diets.includes("Vegan") && limit_tags.includes("Basic")) {
        warnings.push("Simple Recipes are not yet available for vegan diets.");
    } else if (avoidances.includes("wheat") && limit_tags.includes("Basic")) {
        warnings.push("Simple Recipes are not yet available for gluten free diets.");
    } else if (avoidances.includes("milk") && limit_tags.includes("Basic")) {
        warnings.push("Simple Recipes are not yet available for dairy free diets.");
    }

    return { warnings, errors };
}

export function getAssetsFromActivities(activities) {
    const uuids = [];

    activities.forEach((activity) => {
        const { event_data } = activity;

        if (!event_data) {
            return;
        }

        if (event_data.plan_uuid) {
            uuids.push(event_data.plan_uuid);
        }

        if (event_data["uuid"]) {
            uuids.push(event_data["uuid"]);
        }

        if (event_data["Meal Plan ID"]) {
            uuids.push(event_data["Meal Plan ID"]);
        }

        if (event_data["Meal Plan UUID"]) {
            uuids.push(event_data["Meal Plan UUID"]);
        }

        if (event_data.recipe_uuid) {
            uuids.push(event_data.recipe_uuid);
        }

        if (event_data["Recipe ID"]) {
            uuids.push(event_data["Recipe ID"]);
        }

        if (event_data["Recipe UUID"]) {
            uuids.push(event_data["Recipe UUID"]);
        }

        if (event_data["Replaced Recipe ID"]) {
            uuids.push(event_data["Replaced Recipe ID"]);
        }

        if (event_data["Replaced Recipe UUID"]) {
            uuids.push(event_data["Replaced Recipe UUID"]);
        }

        if (event_data["Replaced Side ID"]) {
            uuids.push(event_data["Replaced Side ID"]);
        }

        if (event_data["Replaced Side UUID"]) {
            uuids.push(event_data["Replaced Side UUID"]);
        }

        if (event_data["New Recipe ID"]) {
            uuids.push(event_data["New Recipe ID"]);
        }

        if (event_data["New Recipe UUID"]) {
            uuids.push(event_data["New Recipe UUID"]);
        }

        if (event_data["New Side ID"]) {
            uuids.push(event_data["New Side ID"]);
        }

        if (event_data["New Side UUID"]) {
            uuids.push(event_data["New Side UUID"]);
        }

        if (event_data["exclude_foods"]) {
            ["-", "+"].forEach((delta) => {
                let exclude_foods = [];

                if (typeof event_data["exclude_foods"][delta] === "string") {
                    exclude_foods = event_data["exclude_foods"][delta]
                        .split(",")
                        .map((u) => (u || "").trim())
                        .filter((v) => v);
                } else if (Array.isArray(event_data["exclude_foods"][delta])) {
                    exclude_foods = event_data["exclude_foods"][delta].slice(0);
                }

                exclude_foods.forEach((uuid) => {
                    if (!uuids.includes(uuid)) {
                        uuids.push(uuid);
                    }
                });
            });
        }
    });

    return new Promise((accept, reject) => {
        fetchDocumentsById(uuids).then((documents) => {
            const recipes = indexBy(
                    documents.filter((d) => d.type === "recipe"),
                    "uuid",
                ),
                plans = indexBy(
                    documents.filter((d) => d.type === "plan"),
                    "uuid",
                ),
                collections = indexBy(
                    documents.filter((d) => d.type === "collection"),
                    "uuid",
                ),
                foods = indexBy(
                    documents.filter((d) => d.type === "food"),
                    "uuid",
                );

            return accept({ recipes, plans, collections, foods, ...recipes, ...plans, ...collections, ...foods });
        });
    });
}

export function generateProfileKey(profile) {
    const { preferences = {} } = profile;

    // FA stands for "fix array"
    const fa = (arr) => {
        arr = arr || [];
        arr = arr.filter((v) => v.trim()).sort((a, b) => a.localeCompare(b));

        arr = arr.filter((v, i) => i == arr.indexOf(v));

        return arr.slice(0, 50);
    };

    const family = profile?.family?.map((participant) => ({name: participant.name, portion: participant.portion, uuid: participant.uuid}));

    const profileKey = {
        avoid_cuisines: fa(preferences.avoid_cuisines),
        avoidances: fa(preferences.avoidances),
        avoid_keywords: preferences.avoid_keywords,
        daily_totals: preferences.daily_totals,
        eating_pattern_name: preferences.eating_pattern_name,
        equipment: fa(preferences.equipment),
        exclude_foods: fa(preferences.exclude_foods),
        family,
        inhibit_autopopulate: profile.inhibit_autopopulate,
        limit_tags: fa(preferences.limit_tags),
        leftovers_enabled: preferences.leftovers_enabled ? true : false,
        max_leftover_days: preferences.max_leftover_days,
        meal_kit_providers: fa(preferences.meal_kit_providers),
        meal_types: preferences.meal_types,
        portion: profile.portion,
        prefer_cuisines: fa(preferences.prefer_cuisines),
        prefer_milks: fa(preferences.prefer_milks),
        prefer_rices: fa(preferences.prefer_rices),
        skill_level: preferences.skill_level || '',
    };

    return profileKey;
}

function isEmptySafe(value) {
    if (typeof value === 'number' || typeof value === 'boolean') return false;
    return isEmpty(value);
}


/**
 * Compares a current patient profile with a stored profile key to determine if the profile has changed.
 * Used for meal plan caching - if profile hasn't changed, cached meal plans can be reused.
 *
 * **Key Features:**
 * - **Bidirectional comparison**: Detects both additions and removals
 * - **Semantic equality**: Treats null, undefined, [], {} as equivalent for nutrition data
 * - **Pattern-based meal types**: Compares eating patterns as atomic units
 * - **Performance optimized**: Early return in production, complete analysis in debug mode
 * - **NaN-safe range comparison**: Handles invalid nutrition ranges correctly
 *
 * **Performance Optimizations:**
 * - Early return after first change detected (production mode)
 * - O(n) family member comparison using Maps (was O(n²))
 * - Constant hoisting to reduce memory allocations
 *
 * @param {Object} profile - Current patient profile object
 * @param {Object} profile.preferences - User preferences (avoidances, equipment, etc.)
 * @param {Array} profile.preferences.avoidances - Foods/ingredients to avoid
 * @param {Object} profile.preferences.daily_totals - Nutrition targets by nutrient ID
 * @param {Array} profile.preferences.meal_types - Eating pattern configuration
 * @param {Array} profile.preferences.equipment - Available kitchen equipment
 * @param {string} profile.preferences.skill_level - Cooking skill level
 * @param {Array} profile.family - Family members with portions (accessed as profile[key])
 *
 * @param {Object} profile_key - Stored profile key for comparison
 * @param {Array} profile_key.avoidances - Stored avoidances list
 * @param {Object} profile_key.daily_totals - Stored nutrition targets
 * @param {Array} profile_key.meal_types - Stored eating pattern
 * @param {Array} profile_key.equipment - Stored equipment list
 * @param {string} profile_key.skill_level - Stored skill level
 * @param {Array} profile_key.family - Stored family members
 *
 * @returns {Object} Comparison result object
 * @returns {boolean} returns.isProfileChanged - True if profile has changed
 * @returns {Array<string>} returns.reasons - Detailed list of changes detected
 *
 * @example
 * // Basic usage
 * const result = profileKeyComparator(currentProfile, storedProfile);
 * if (result.isProfileChanged) {
 *     console.log('Profile changed:', result.reasons);
 *     // Regenerate meal plans
 * } else {
 *     // Use cached meal plans
 * }
 *
 * @example
 * // Typical result when profile changed
 * {
 *   isProfileChanged: true,
 *   reasons: [
 *     'Daily Totals [203] changed: min: 50, max: 100 → min: 60, max: 120',
 *     'Eating pattern changed: [Breakfast, Lunch] → [Breakfast, Brunch]',
 *     'Family member [Jane] removed',
 *     '[equipment] preferences changed (added: microwave)'
 *   ]
 * }
 *
 * @since 2025-07-28 - Major refactor with performance optimizations and comprehensive fixes
 * @see {@link /docs/PROFILEKEYCOMPARATOR_IMPLEMENTATION_NOTES.md} - Detailed implementation notes
 */
export function profileKeyComparator(profile, profile_key) {
    let isProfileChanged = false;
    const reasons = [];

    // Debug logging - uncomment the line below to enable detailed logging
    // const DEBUG_PROFILE_COMPARISON = true;
    const DEBUG_PROFILE_COMPARISON = false;

    /**
     * Internal debug logging helper - logs detailed comparison data when DEBUG_PROFILE_COMPARISON is enabled
     * @param {Object} logData - Debug information to log
     * @private
     */
    const debugLog = (logData) => {
        if (DEBUG_PROFILE_COMPARISON) {
            console.log('ProfileKeyComparator Debug:', logData);
        }
    };

    /**
     * NaN-safe nutrition range comparison helper
     * Compares min/max values in nutrition ranges while handling NaN values correctly
     * Treats null, undefined, and missing properties as equivalent (all mean "not set")
     *
     * @param {Object} a - First range object {min: number, max: number}
     * @param {Object} b - Second range object {min: number, max: number}
     * @param {string} location - Context location for debug logging (e.g., 'daily_totals[203]')
     * @returns {boolean} True if ranges are different, false if same
     * @private
     */
    const cmpRanges = (a, b, location = 'unknown') => {
        // Helper to normalize null/undefined to a consistent value for comparison
        const normalize = (val) => (val == null ? undefined : val);

        return !isEqual(normalize(a?.min), normalize(b?.min)) ||
               !isEqual(normalize(a?.max), normalize(b?.max));
    };

    /**
     * Formats a nutrition range object for display in reason messages
     * @param {Object} range - Range object with min/max properties
     * @param {number} [range.min] - Minimum value
     * @param {number} [range.max] - Maximum value
     * @returns {string} Formatted range string (e.g., "min: 50, max: 100")
     * @private
     */
    const printRange = (range = {}) => {
        return [
            typeof range.min === "number" ? `min: ${range.min}` : false,
            typeof range.max === "number" ? `max: ${range.max}` : false,
        ]
            .filter((v) => v !== false)
            .join(", ");
    };

    // Guard clause: validate required parameters
    if (!profile_key || !profile) {
        // console.log({f: 'profileKeyComparator rejected', profile, profile_key});
        return { isProfileChanged, reasons: ['missing profile or profile key'] };
    }

    /**
     * Complete list of profile properties to compare
     * Order matters for performance - properties likely to change should be first for early return
     */
    const keysToCmp = [
        'avoid_cuisines', 'avoidances', 'avoid_keywords', 'daily_totals', 'equipment', 'exclude_foods', 'family', 'limit_tags', 'leftovers_enabled',
        'portion', 'prefer_cuisines', 'prefer_milks', 'prefer_rices', 'meal_kit_providers', 'meal_types', 'max_leftover_days', 'skill_level', 'eating_pattern_name'
    ];

    /**
     * Performance optimization: hoist static arrays outside loops to avoid recreation
     * These constants are used in nested loops and would otherwise be recreated on each iteration
     */
    const mealTypeKeys = ['side_dish', 'begin', 'end', 'pick_seq', 'keywords', 'max_time', 'max_cost', 'participants'];
    const familyKeysToCmp = ['uuid', 'name', 'portion'];

    /**
     * Main comparison loop with performance optimization
     * Uses for...of instead of forEach to enable true early return from function
     * In production mode, exits immediately after first change detected for performance
     * In debug mode, continues checking all properties for complete analysis
     */
    mainLoop: for (const key of keysToCmp) {
        // if both are nullish or empty then skip
        // Some properties (family, portion) are stored on profile root, not preferences
        const currentValue = (key === 'family' || key === 'portion') ? profile[key] : profile.preferences[key];
        const keyValue = profile_key[key];

        if (isEmptySafe(currentValue) && isEmptySafe(keyValue)) {
            continue;
        }

        if (key == "daily_totals") {
            const dailyTotals = profile.preferences[key] || {};
            const dailyTotalsFromKey = profile_key[key] || {};

            // Get all nutrients that exist in either profile (bidirectional check)
            const allNutrients = new Set([...Object.keys(dailyTotals), ...Object.keys(dailyTotalsFromKey)]);

            for (const nutrNo of allNutrients) {
                const currentRange = dailyTotals[nutrNo];
                const keyRange = dailyTotalsFromKey[nutrNo];

                // Skip if both ranges are empty
                if (isEmptySafe(currentRange) && isEmptySafe(keyRange)) {
                    continue;
                }

                // Check if nutrient was removed from current profile
                if (!currentRange && keyRange) {
                    isProfileChanged = true;
                    const reason = `Daily Totals [${nutrNo}] removed (was: ${printRange(keyRange)})`;
                    reasons.push(reason);
                    debugLog({
                        section: 'daily_totals_removal',
                        nutrNo,
                        removedRange: keyRange,
                        reason
                    });

                    // Performance: early return in production mode
                    if (!DEBUG_PROFILE_COMPARISON) {
                        return { isProfileChanged, reasons };
                    }
                    continue;
                }

                // Check if nutrient was added to current profile
                if (currentRange && !keyRange) {
                    isProfileChanged = true;
                    const reason = `Daily Totals [${nutrNo}] added (${printRange(currentRange)})`;
                    reasons.push(reason);
                    debugLog({
                        section: 'daily_totals_addition',
                        nutrNo,
                        addedRange: currentRange,
                        reason
                    });

                    // Performance: early return in production mode
                    if (!DEBUG_PROFILE_COMPARISON) {
                        return { isProfileChanged, reasons };
                    }
                    continue;
                }

                // Check if nutrient range was modified
                if (currentRange && keyRange && cmpRanges(currentRange, keyRange, `daily_totals[${nutrNo}]`)) {
                    isProfileChanged = true;
                    const reason = `Daily Totals [${nutrNo}] changed from [${printRange(keyRange)}] => [${printRange(currentRange)}]`;
                    reasons.push(reason);
                    debugLog({
                        section: 'daily_totals_modification',
                        nutrNo,
                        value_from_profile: currentRange,
                        value_from_key: keyRange,
                        reason
                    });

                    // Performance: early return in production mode
                    if (!DEBUG_PROFILE_COMPARISON) {
                        return { isProfileChanged, reasons };
                    }
                }
            }
        } else if (key == "meal_types") {
            const currentPattern = profile.preferences.meal_types || [];
            const keyPattern = profile_key.meal_types || [];

            // If patterns are different lengths, they're different eating patterns
            if (currentPattern.length !== keyPattern.length) {
                isProfileChanged = true;
                const reason = `Eating pattern changed: ${keyPattern.length} meals → ${currentPattern.length} meals`;
                reasons.push(reason);
                debugLog({
                    section: 'meal_types_length_change',
                    isProfileChanged,
                    reason
                });

                // Performance: early return in production mode
                if (!DEBUG_PROFILE_COMPARISON) {
                    return { isProfileChanged, reasons };
                }
                continue mainLoop;
            }

            // Compare each position for pattern changes (order matters for eating schedule)
            let patternChanged = false;
            const patternDetails = [];

            mealLoop: for (const [index, mealType] of currentPattern.entries()) {
                // Bounds check - should not happen due to length check above, but safety first
                if (index >= keyPattern.length) {
                    patternChanged = true;
                    const detail = `Extra meal at position ${index + 1}: ${mealType.name || mealType.main_dish}`;
                    patternDetails.push(detail);
                    debugLog({
                        section: 'meal_types_bounds_error',
                        index,
                        mealType: mealType.name || mealType.main_dish,
                        detail
                    });
                    continue mealLoop;
                }

                const keyMealType = keyPattern[index];

                // Compare meal type identity (name, main_dish are key identifiers)
                if (mealType.name !== keyMealType.name || mealType.main_dish !== keyMealType.main_dish) {
                    patternChanged = true;
                    const currentName = mealType.name || mealType.main_dish || `Meal ${index + 1}`;
                    const keyName = keyMealType.name || keyMealType.main_dish || `Meal ${index + 1}`;
                    const detail = `Position ${index + 1}: ${keyName} → ${currentName}`;
                    patternDetails.push(detail);
                    debugLog({
                        section: 'meal_types_identity_change',
                        index,
                        currentName,
                        keyName,
                        detail
                    });
                    continue mealLoop;
                }

                // Compare meal-specific properties that affect meal planning
                propertyLoop: for (const mtkey of mealTypeKeys) {
                    // Special handling for participants - use isEmptySafe
                    if (mtkey === 'participants') {
                        // Skip if both participants arrays are empty
                        if (isEmptySafe(mealType[mtkey]) && isEmptySafe(keyMealType[mtkey])) {
                            continue propertyLoop;
                        }
                    }

                    // Direct comparison for all properties
                    if (!isEqual(mealType[mtkey], keyMealType[mtkey])) {
                        patternChanged = true;
                        const mealName = mealType.name || mealType.main_dish || `Meal ${index + 1}`;
                        const detail = `${mealName} ${mtkey}: ${JSON.stringify(keyMealType[mtkey])} → ${JSON.stringify(mealType[mtkey])}`;
                        patternDetails.push(detail);
                        debugLog({
                            section: 'meal_types_property_change',
                            mealName,
                            property: mtkey,
                            value_from_profile: mealType[mtkey],
                            value_from_key: keyMealType[mtkey],
                            detail
                        });
                    }
                }

                // Compare nutritional envelopes - bidirectional check for all nutrients
                const envelope = mealType.envelope || {};
                const keyEnvelope = keyMealType.envelope || {};

                // Get all nutrients that exist in either envelope
                const allNutrNos = new Set([...Object.keys(envelope), ...Object.keys(keyEnvelope)]);

                for (const nutrNo of allNutrNos) {
                    // Skip if both envelope ranges are empty
                    if (isEmptySafe(envelope[nutrNo]) && isEmptySafe(keyEnvelope[nutrNo])) {
                        continue;
                    }

                    if (cmpRanges(envelope[nutrNo], keyEnvelope[nutrNo], `meal_envelope[${mealType.name}][${nutrNo}]`)) {
                        patternChanged = true;
                        const mealName = mealType.name || mealType.main_dish || `Meal ${index + 1}`;
                        const detail = `${mealName} nutrition [${nutrNo}]: ${printRange(keyEnvelope[nutrNo])} → ${printRange(envelope[nutrNo])}`;
                        patternDetails.push(detail);
                        debugLog({
                            section: 'meal_types_nutrition_change',
                            mealName,
                            nutrNo,
                            value_from_profile: envelope[nutrNo],
                            value_from_key: keyEnvelope[nutrNo],
                            detail
                        });
                    }
                }
            }

            if (patternChanged) {
                isProfileChanged = true;
                const currentNames = currentPattern.map(mt => mt.name || mt.main_dish).join(', ');
                const keyNames = keyPattern.map(mt => mt.name || mt.main_dish).join(', ');
                const mainReason = `Eating pattern changed: [${keyNames}] → [${currentNames}]`;
                reasons.push(mainReason);

                // Add specific details if helpful for debugging
                if (patternDetails.length > 0) {
                    reasons.push(...patternDetails.slice(0, 3)); // Limit to first 3 details to avoid spam
                }

                debugLog({
                    section: 'meal_types_final_change',
                    isProfileChanged,
                    mainReason,
                    patternDetailsCount: patternDetails.length,
                    patternDetails: patternDetails
                });

                // Performance: early return in production mode
                if (!DEBUG_PROFILE_COMPARISON) {
                    return { isProfileChanged, reasons };
                }
            }
        } else if (key == "family" && !isEqual((profile[key]), (profile_key[key]))) {
            const family = profile[key] || [];
            const familyFromKey = profile_key[key] || [];

            // Performance optimization: Convert arrays to Maps for O(1) lookups (was O(n²), now O(n))
            const familyMap = new Map(family.map(member => [member.uuid, member]));
            const familyFromKeyMap = new Map(familyFromKey.map(member => [member.uuid, member]));

            // Check for additions and modifications (current → key)
            familyAddLoop: for (const member of family) {
                const familyMember = familyFromKeyMap.get(member.uuid);

                if (!familyMember) {
                    isProfileChanged = true;
                    const reason = `New family member [${member.name}] added`;
                    reasons.push(reason);
                    debugLog({
                        section: 'family_addition',
                        addedMember: { uuid: member.uuid, name: member.name },
                        reason
                    });

                    // Performance: early return in production mode
                    if (!DEBUG_PROFILE_COMPARISON) {
                        return { isProfileChanged, reasons };
                    }
                    continue familyAddLoop;
                }

                for (const fmkey of familyKeysToCmp) {
                    // Direct comparison for family member properties
                    if (!isEqual(member[fmkey], familyMember[fmkey])) {
                        isProfileChanged = true;
                        const reason = `[${member.name}] [${fmkey}] changed from ${familyMember[fmkey]} => ${member[fmkey]}`;
                        reasons.push(reason);
                        debugLog({
                            section: 'family_modification',
                            memberName: member.name,
                            property: fmkey,
                            value_from_profile: member[fmkey],
                            value_from_key: familyMember[fmkey],
                            reason
                        });

                        // Performance: early return in production mode
                        if (!DEBUG_PROFILE_COMPARISON) {
                            return { isProfileChanged, reasons };
                        }
                    }
                }
            }

            // Check for removals (key → current) - this was missing!
            for (const keyMember of familyFromKey) {
                const currentMember = familyMap.get(keyMember.uuid);

                if (!currentMember) {
                    isProfileChanged = true;
                    const reason = `Family member [${keyMember.name}] removed`;
                    reasons.push(reason);
                    debugLog({
                        section: 'family_removal',
                        removedMember: { uuid: keyMember.uuid, name: keyMember.name },
                        reason
                    });

                    // Performance: early return in production mode
                    if (!DEBUG_PROFILE_COMPARISON) {
                        return { isProfileChanged, reasons };
                    }
                }
            }
        } else if (!isEqual(currentValue, keyValue)) {
            // Most preference arrays are sets where order doesn't matter for meal planning
            const unorderedArrays = ['avoid_cuisines', 'avoidances', 'avoid_keywords', 'equipment',
                                   'exclude_foods', 'limit_tags', 'prefer_cuisines', 'prefer_milks', 'prefer_rices'];

            if (unorderedArrays.includes(key) && Array.isArray(currentValue) && Array.isArray(keyValue)) {
                const currentSet = new Set(currentValue);
                const keySet = new Set(keyValue);

                if (currentSet.size === keySet.size && [...currentSet].every(item => keySet.has(item))) {
                    continue; // Just reordered, not actually changed
                }
            }
            isProfileChanged = true;

            // Provide more detailed reasons for array changes
            if (Array.isArray(currentValue) && Array.isArray(keyValue)) {
                const added = currentValue.filter(item => !keyValue.includes(item));
                const removed = keyValue.filter(item => !currentValue.includes(item));

                let detailedReason = `[${key}] preferences changed`;
                if (added.length > 0) {
                    detailedReason += ` (added: ${added.join(', ')})`;
                }
                if (removed.length > 0) {
                    detailedReason += ` (removed: ${removed.join(', ')})`;
                }
                if (added.length === 0 && removed.length === 0) {
                    detailedReason += ` (reordered or modified)`;
                }

                reasons.push(detailedReason);

                debugLog({
                    section: 'array_preferences_change',
                    key,
                    currentLength: currentValue.length,
                    keyLength: keyValue.length,
                    added,
                    removed,
                    value_from_profile: currentValue,
                    value_from_key: keyValue,
                    reason: detailedReason
                });

                // Performance: early return in production mode
                if (!DEBUG_PROFILE_COMPARISON) {
                    return { isProfileChanged, reasons };
                }
            } else {
                // Non-array properties
                const reason = `[${key}] preferences changed from [${JSON.stringify(keyValue)}] => [${JSON.stringify(currentValue)}]`;
                reasons.push(reason);

                debugLog({
                    section: 'general_preferences_change',
                    key,
                    value_from_profile: currentValue,
                    value_from_key: keyValue,
                    reason
                });

                // Performance: early return in production mode
                if (!DEBUG_PROFILE_COMPARISON) {
                    return { isProfileChanged, reasons };
                }
            }
        }
    }

    return { isProfileChanged, reasons };
}

/**
 * Get DRI values for a given profile based on demographic
 * @param {Object} profile - User profile with birthdate and gender
 * @returns {Object} DRI values for the profile's demographic
 */
export function getDRIForProfile(profile) {
    if (!profile) {
        return allRecommendations['all-ages'] || {};
    }

    const demographic = getDemographic(profile);
    return {
        ...allRecommendations['all-ages'],
        ...allRecommendations[demographic]
    };
}

/**
 * Calculate visual range for a nutrient based on its prescription and DRI
 * @param {Object} range - Nutrient prescription with min/max values
 * @param {number} driValue - DRI value for the nutrient
 * @returns {Object} Visual range with start and end percentages
 */
export function calculateNutrientVisualRange(range, driValue) {
    let start = 0, end = 100;

    if (!range) {
        return { start, end };
    }

    if (range.min && range.max) {
        // Both min and max defined - show the range
        if (driValue > 0) {
            // Use DRI as reference point (DRI = 50% on scale)
            // Scale factor: 0% = 0, 50% = DRI, 100% = 2x DRI
            const scaleFactor = driValue * 2;

            // Calculate positions
            start = Math.round((range.min / scaleFactor) * 100);
            end = Math.round((range.max / scaleFactor) * 100);

            // Constrain to reasonable visual bounds
            start = Math.min(90, Math.max(5, start));
            end = Math.min(95, Math.max(start + 5, end));
        } else {
            // No DRI available - use proportional positioning
            // Assume max value represents ~75% of scale for visual balance
            if (range.max > 0) {
                const scale = range.max / 0.75;
                start = Math.round((range.min / scale) * 100);
                end = Math.round((range.max / scale) * 100);
                start = Math.min(90, Math.max(5, start));
                end = Math.min(95, Math.max(start + 5, end));
            } else {
                // Fallback to fixed positions
                start = 30;
                end = 70;
            }
        }
    } else if (range.min && !range.max) {
        // Only minimum defined - show from min to end
        if (driValue > 0) {
            const scaleFactor = driValue * 2;
            start = Math.round((range.min / scaleFactor) * 100);
            start = Math.min(90, Math.max(5, start));
            end = 100; // Extend to end since no max limit
        } else {
            // Position at 50% for visibility when no DRI
            start = 50;
            end = 100;
        }
    } else if (!range.min && range.max) {
        // Only maximum defined - show from start to max
        start = 0;
        if (driValue > 0) {
            const scaleFactor = driValue * 2;
            end = Math.round((range.max / scaleFactor) * 100);
            end = Math.min(95, Math.max(10, end));
        } else if (range.max > 0) {
            // Use max as ~50% of scale for visual balance
            end = 50;
        } else {
            end = 50;
        }
    } else {
        // Neither min nor max (tracking only)
        // Show full bar to indicate tracking without limits
        start = 0;
        end = 100;
    }

    return { start, end };
}

export function getPrescriptionsFromProfile(profile) {
    const prescriptions = {}, macros = {};

    if (!profile) {
        return prescriptions;
    }

    const allDayRx = profile.preferences.daily_totals;
    const mealTypes = profile.preferences.meal_types;

    if (!allDayRx) {
        return prescriptions;
    }

    // Get DRI values for this profile
    const dri = getDRIForProfile(profile);

    prescriptions['Daily Totals'] = {};
    Object.keys(allDayRx).forEach(nutrNo => {
        const range = allDayRx[nutrNo];
        const driValue = dri[nutrNo] || 0;
        const { start, end } = calculateNutrientVisualRange(range, driValue);

        // Extract macros for pie chart
        if (['203', '204', '205', '208'].includes(nutrNo)) {
            if (range.min && !range.max) {
                macros[nutrNo] = range.min;
            } else if (!range.min && range.max) {
                macros[nutrNo] = range.max;
            } else if (range.min && range.max) {
                macros[nutrNo] = (range.min + range.max) / 2;
            }
        }

        prescriptions['Daily Totals'][nutrNo] = {
            start,
            end,
            ...range,
        }
    });

    mealTypes.forEach((mealType) => {
        const envelope = mealType.envelope;

        if (!envelope) {
            return;
        }

        prescriptions[mealType.name] = {};
        Object.keys(envelope).forEach(nutrNo => {
            const range = envelope[nutrNo];

            if (!range.min && !range.max) {
                return;
            }

            const driValue = dri[nutrNo] || 0;
            const { start, end } = calculateNutrientVisualRange(range, driValue);

            prescriptions[mealType.name][nutrNo] = {
                start,
                end,
                ...range,
            }
        });
    });

    return { prescriptions, macros };
}

export function generateFamilyMembers(memberCount, prefix, template = {}, startAt = 1) {
    let i = startAt; // Starts at 1 because the user is considered the first family member
    let members = [];
    for (; i < memberCount; i++) {
        members.push({
            uuid: uuid.v4(),
            name: prefix + (i + 1),
            ...template,
        });
    }

    return members;
}

export function generateProfile(profile, user) {
    const { conditions, diets, avoidances, exclude_foods, birthdate, gender, weight_kg, weight_lbs, height_cm, units_mode, goal_weight_kg, goal_weight_lbs,
            weekly_goal_kcal, activity_level, target_energy_kcal, portion, due_date, fetus_count, adultsInFamily, childrenInFamily, completed, shopping_freq, skill_level, preferences } = profile;

    // We need the alternate user to inherit these two fields from the currently logged in user
    const { hide_nutrition = false, inhibit_swap = false } = user || {};

    const adults = generateFamilyMembers(
        adultsInFamily,
        'Adult #',
        {type: 'adult', portion: 1} // 29 year old adults
    );

    const children = generateFamilyMembers(
        childrenInFamily,
        'Child #',
        {type: 'child', portion: 0.5}, // 7 year old children
        0, // children are not a first member of the family
    );

    // Create the "alternate" user. This user is a fake user that is stored temporarily in local storage
    const altUser = {
        name: user.name,
        first_name: user.first_name,
        last_name: user.last_name,
        conditions,
        preferences: {
            ...preferences,
            diets,
            avoidances,
            exclude_foods,
            leftovers_enabled: true,
            max_leftover_days: 1,
            equipment: ['Oven', 'Stovetop', 'Microwave', 'Blender'],
            skill_level,
        },
        family: adults.concat(children),
        birthdate,
        gender,
        weight_kg,
        weight_lbs,
        height_cm,
        target_energy_kcal,
        portion,
        due_date,
        fetus_count,
        activity_level,
        goal_weight_kg,
        goal_weight_lbs,
        weekly_goal_kcal,
        units_mode,
        completed,
        hide_nutrition,
        inhibit_swap,
        portion_resolution: user.portion_resolution,
        shopping_freq,
        role: 'admin'
    };

    return altUser;
}

/**
 * Creates a default profile for Try It Out mode.
 * Preserves user's wizard choices (conditions, diets, avoidances)
 * but uses default values for everything else to avoid using PRO user data.
 *
 * @param {Object} wizardChoices - The profile from the wizard with user's selections
 * @returns {Object} A clean profile with only wizard choices and default values
 */
export const getDefaultTryItOutProfile = (wizardChoices = {}) => ({
    name: 'Quick Start',
    conditions: wizardChoices?.conditions || [],
    preferences: {
        // User selections from form
        diets: wizardChoices?.preferences?.diets || [],
        avoidances: wizardChoices?.preferences?.avoidances || [],
        exclude_foods: wizardChoices?.preferences?.exclude_foods || [],
        skill_level: wizardChoices?.preferences?.skill_level || 'Beginner',
        // API fields - KEEP HARDCODED so MealDetails calls API
        eating_pattern_name: null,
        meal_types: [],
        daily_totals: {},
    },
    // User form inputs - PRESERVE from wizardChoices
    adultsInFamily: wizardChoices?.adultsInFamily ?? 1,
    shopping_freq: wizardChoices?.shopping_freq ?? 7,
    childrenInFamily: wizardChoices?.childrenInFamily ?? 0,
    units_mode: wizardChoices?.units_mode || 'english',
    target_energy_kcal: wizardChoices?.target_energy_kcal ?? 2000,
    portion: wizardChoices?.portion ?? 1,
    weight_kg: wizardChoices?.weight_kg ?? 70,
    weight_lbs: wizardChoices?.weight_lbs ?? null,
    goal_weight_lbs: wizardChoices?.goal_weight_lbs ?? null,
    height_cm: wizardChoices?.height_cm ?? 170,
    gender: wizardChoices?.gender || 'male',
    birthdate: wizardChoices?.birthdate || '1990-01-01',
    // Always reset for Try It Out mode
    family: [],
    first_name: 'User',
    hide_nutrition: false,
    completed: [],
});
